import Head from 'next/head';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import SearchBar from '../components/SearchBar';
import TrustBadges from '../components/TrustBadges';
import HomepageContent from '../components/HomepageContent';
import FloodAlertForm from '../components/FloodAlertForm';
// Lazy load schema components (not critical for initial render)
const SchemaComponents = dynamic(() => import('../components/schema'), {
  ssr: true
});
import config from '../config';

// Lazy load heavy components
const EmergencyGuide = dynamic(() => import('../components/EmergencyGuide'), {
  loading: () => (
    <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded mb-4"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-start">
            <div className="w-8 h-8 bg-gray-200 rounded mr-4"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ),
  ssr: true
});

const ChatbotWidget = dynamic(() => import('../components/ChatbotWidget'), {
  loading: () => (
    <div className="fixed bottom-6 right-6 w-12 h-12 bg-[#004C8C] rounded-full shadow-lg animate-pulse"></div>
  ),
  ssr: false
});

export default function Home() {
  return (
    <>
      <Head>
        <title>Water Damage Direct | Find Local Water Damage Restoration Contractors</title>
        <meta name="description" content="Find trusted local water damage restoration contractors for 24/7 emergency services. Get help with flood cleanup, water extraction, and mold remediation." />
        <link rel="canonical" href={`${config.siteUrl}/`} />
      </Head>
      {/* Hero Section */}
      <section className="bg-[#004C8C] text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Emergency Water Damage Restoration Services
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-blue-100">
              Find trusted local professionals for immediate water damage cleanup and restoration.
            </p>
            <SearchBar className="mb-8" />
            <div className="text-sm text-blue-200">
              Available 24/7
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Why Choose Water Damage Direct?
          </h2>
          <TrustBadges />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Emergency Restoration Services Available
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="text-[#004C8C] mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Water Extraction</h3>
              <p className="text-gray-600 mb-4">
                Professional grade equipment to quickly remove standing water from your property, minimizing damage.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Flood water removal
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Basement flooding
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Sewage backup
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="text-[#004C8C] mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Drying & Dehumidification</h3>
              <p className="text-gray-600 mb-4">
                Industrial-strength air movers and dehumidifiers to thoroughly dry your property and prevent mold growth.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Structural drying
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Carpet & padding drying
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Moisture detection
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="text-[#004C8C] mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Restoration & Repairs</h3>
              <p className="text-gray-600 mb-4">
                Full-service restoration to return your property to its pre-damage condition, from minor repairs to major reconstruction.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Drywall replacement
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Flooring restoration
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#38A169] mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Mold remediation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* New Content Section */}
      <HomepageContent />

      {/* Emergency Guide Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <EmergencyGuide />
        </div>
      </section>

      {/* Chatbot Widget */}
      <ChatbotWidget />
      
      {/* JSON-LD Structured Data - Lazy loaded */}
      <SchemaComponents 
        socialLinks={[
          "https://facebook.com/waterdamagedirect",
          "https://twitter.com/waterdamagedirect"
        ]}
        faqs={[
          {
            question: "What should I do first after water damage?",
            answer: "First, ensure your safety by turning off electricity if water is near electrical devices. Then, if possible, stop the water source. Contact Water Damage Direct to find a professional restoration service immediately."
          },
          {
            question: "How quickly should water damage be addressed?",
            answer: "Water damage should be addressed immediately. Within 24-48 hours, mold can begin to grow. The longer water sits, the more extensive and expensive the damage becomes."
          },
          {
            question: "Does insurance cover water damage restoration?",
            answer: "Most homeowner's insurance policies cover sudden and accidental water damage. However, coverage varies by policy and situation. Our partnered contractors can help you navigate the insurance claims process."
          }
        ]}
      />
    </>
  );
}
